import axios from 'axios';
import { KeycloakService } from './KeycloakService';
import { decodeBase64Url, encodeBase64Url } from '../components/Utils';
import Config from '../config/Config';

// Create instance of axios client for DPP Manager
export const apiClient = axios.create({
  baseURL: Config.dppManagerService.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to add token to requests
apiClient.interceptors.request.use(
  async (config) => {
    // Ensure token is refreshed before each request
    try {
      await refreshKeycloakToken(); // Refresh Keycloak token
    } catch (error) {
      console.error('Error refreshing Keycloak token:', error);
      KeycloakService.login(); // If refresh fails, trigger login
    }

    // Add the token to the authorization header
    const token = KeycloakService.getToken(); // Get token from KeycloakService
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Function to refresh Keycloak token
const refreshKeycloakToken = async () => {
  if (KeycloakService.isAuthenticated()) {
    await KeycloakService.refreshToken(); // Refresh the token
  }
};

export const fetchAasEnvironment = async (serialNumber) => {
  try {
    const decodedSerialNumber = decodeBase64Url(serialNumber);
    const response = await apiClient.get(`/api/v1/shells/lookup/serialNumber/${decodedSerialNumber}`);
    
    if (!response.data) {
      throw new Error('No AAS environment found');
    }
    
    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      if (status === 403) {
        throw new Error('Error 403. You do not have permission to access this resource.');
      } else if (status === 401) {
        throw new Error('Error 401. Unauthorized. Please log in again.');
      } else if (status === 404) {
        throw new Error('Error 404. Resource not found.');
      }
    }
    throw new Error('Error fetching data. Please try again.');
  }
};

export const fetchSubmodelDescriptors = async (aasId) => {
  try {
    if (!aasId) {
      throw new Error('No AAS ID provided');
    }

    const encodedAasId = encodeBase64Url(aasId);
    const response = await apiClient.get(`/api/v1/shells/${encodedAasId}/submodel-descriptors`);

    if (!response.data) {
      throw new Error('No submodel descriptors found');
    }

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Error fetching submodel descriptors. Please try again.');
  }
};

export const fetchConceptDescription = async (cdId) => {
  try {
    if (!cdId) {
      throw new Error('No concept description ID provided');
    }

    const encodedCdId = encodeBase64Url(cdId);
    const response = await apiClient.get(`/api/v1/concept-descriptions/${encodedCdId}`);

    if (!response.data) {
      throw new Error('No concept description data found');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to fetch concept description. Please try again.');
  }
};

export const fetchSubmodel = async (smId) => {
  try {
    if (!smId) {
      throw new Error('No submodel ID provided');
    }

    const encodedSmId = encodeBase64Url(smId);
    const response = await apiClient.get(`/api/v1/submodels/${encodedSmId}`);

    if (!response.data) {
      throw new Error('No submodel data found');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to fetch submodel. Please try again.');
  }
};

export const fetchSubmodelElement = async (descriptor, idShortPath, options = {}) => {
  const { signal } = options;
  try {
    if (!descriptor?.id || !idShortPath) {
      throw new Error('Invalid descriptor or idShortPath');
    }

    const encodedSmId = encodeBase64Url(descriptor.id);
    const response = await apiClient.get(
      `/api/v1/submodels/${encodedSmId}/submodel-elements/${idShortPath}`,
      { signal }
    );

    if (!response.data) {
      throw new Error('No submodel element found');
    }

    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error; // let callers handle cancellation themselves
    }
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`HTTP ${status}: ${message}`);
    }
    console.error('fetchSubmodelElement failed:', error);
    return null;
  }
};

export const fetchSerializedAasEnvironment = async (aasId) => {
  try {
    if (!aasId) {
      throw new Error('No AAS ID provided');
    }

    const encodedAasId = encodeBase64Url(aasId);
    const response = await apiClient.get(
      `/api/v1/serialization/${encodedAasId}?includeConceptDescriptions=false`
    );

    if (!response.data) {
      throw new Error('No serialized AAS environment found');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      if (status === 403) {
        throw new Error('Error 403. You do not have permission to access this resource.');
      } else if (status === 401) {
        throw new Error('Error 401. Unauthorized. Please log in again.');
      } else if (status === 404) {
        throw new Error('Error 404. Resource not found.');
      }
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Error fetching serialized AAS environment. Please try again.');
  }
};


export const createSubmodelElement = async (smId, body) => {
  try {
    if (!smId || !body) {
      throw new Error('Submodel ID and body are required');
    }

    const encodedSmId = encodeBase64Url(smId);
    const response = await apiClient.post(`/api/v1/submodels/${encodedSmId}/submodel-elements`, body);

    if (!response.data) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      if (status === 403) {
        throw new Error('Error 403. You do not have permission to create this resource.');
      } else if (status === 401) {
        throw new Error('Error 401. Unauthorized. Please log in again.');
      } else if (status === 400) {
        throw new Error('Error 400. Invalid request data.');
      }
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to create submodel element. Please try again.');
  }
};

export const createSubmodelElementAtPath = async (smId, idShortPath, body) => {
  try {
    if (!smId || !idShortPath || !body) {
      throw new Error('Submodel ID, idShortPath, and body are required');
    }

    const encodedSmId = encodeBase64Url(smId);
    const response = await apiClient.post(
      `/api/v1/submodels/${encodedSmId}/submodel-elements/${idShortPath}`,
      body
    );

    if (!response.data) {
      throw new Error('No response data received');
    }

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      if (status === 403) {
        throw new Error('Error 403. You do not have permission to create this resource.');
      } else if (status === 401) {
        throw new Error('Error 401. Unauthorized. Please log in again.');
      } else if (status === 400) {
        throw new Error('Error 400. Invalid request data.');
      }
      throw new Error(`HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to create submodel element at path. Please try again.');
  }
};

export const patchSubmodelElementValue = async (smId, idShortPath, value) => {
  try {
    if (!smId || !idShortPath === undefined) {
      throw new Error('submodelId and idShortPath are required');
    }

    const payload = JSON.stringify(String(value));
    const encodedSmId = encodeBase64Url(smId);
    const endpoint = `/api/v1/submodels/${encodedSmId}/submodel-elements/${idShortPath}/$value`;

    const response = await apiClient.patch(endpoint, payload);

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;

      if (status === 403) {
        throw new Error('Error 403. You do not have permission to update this resource.');
      } else if (status === 401) {
        throw new Error('Error 401. Unauthorized. Please log in again.');
      } else if (status === 404) {
        throw new Error('Error 404. Submodel element not found.');
      } else if (status === 400) {
        throw new Error('Error 400. Invalid value or request.');
      }

      throw new Error(`HTTP ${status}: ${message}`);
    }

    throw new Error('Failed to update submodel element value. Please try again.');
  }
};

export const copyShellDescriptor = async (aasId, sourceRegistryId, targetRegistryId) => {
  try {
    if (!aasId) throw new Error("No AAS ID provided");

    const encodedAasId = encodeBase64Url(aasId);

    const response = await apiClient.post(
      `/api/v1/shell-descriptors/${encodedAasId}/copy`,
      {},
      {
        params: {
          sourceRegistryId,
          targetRegistryId,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to copy shell descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to copy shell descriptor. Please try again.');
  }
};

export const copySubmodelDescriptor = async (submodelId, sourceRegistryId, targetRegistryId) => {
  try {
    if (!submodelId) throw new Error("No Submodel ID provided");

    const encodedSmId = encodeBase64Url(submodelId);

    const response = await apiClient.post(
      `/api/v1/submodel-descriptors/${encodedSmId}/copy`,
      {},
      {
        params: {
          sourceRegistryId,
          targetRegistryId,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to copy submodel descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error('Failed to copy submodel descriptor. Please try again.');
  }
};

export const getShellDescriptor = async (aasId, registryId) => {
  try {
    if (!aasId) throw new Error("No AAS ID provided");
    if (!registryId) throw new Error("No Registry ID provided");

    const encodedAasId = encodeBase64Url(aasId);

    // Perform GET request for shell descriptor
    const response = await apiClient.get(
      `/api/v1/shell-descriptors/${encodedAasId}`,
      {
        params: { registryId },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to get shell descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error("Failed to get shell descriptor. Please try again.");
  }
};

export const deleteShellDescriptor = async (aasId, registryId) => {
  try {
    if (!aasId) throw new Error("No AAS ID provided");
    if (!registryId) throw new Error("No Registry ID provided");

    const encodedAasId = encodeBase64Url(aasId);

    const response = await apiClient.delete(
      `/api/v1/shell-descriptors/${encodedAasId}`,
      {
        params: { registryId },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to delete shell descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error("Failed to delete shell descriptor. Please try again.");
  }
};

export const getSubmodelDescriptor = async (submodelId, registryId) => {
  try {
    if (!submodelId) throw new Error("No Submodel ID provided");
    if (!registryId) throw new Error("No Registry ID provided");

    const encodedSmId = encodeBase64Url(submodelId);

    // Perform GET request for submodel descriptor
    const response = await apiClient.get(
      `/api/v1/submodel-descriptors/${encodedSmId}`,
      {
        params: { registryId },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to get submodel descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error("Failed to get submodel descriptor. Please try again.");
  }
};

export const deleteSubmodelDescriptor = async (submodelId, registryId) => {
  try {
    if (!submodelId) throw new Error("No Submodel ID provided");
    if (!registryId) throw new Error("No Registry ID provided");

    const encodedSmId = encodeBase64Url(submodelId);

    const response = await apiClient.delete(
      `/api/v1/submodel-descriptors/${encodedSmId}`,
      {
        params: { registryId },
      }
    );

    return response.data;
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to delete submodel descriptor. HTTP ${status}: ${message}`);
    }
    throw new Error("Failed to delete submodel descriptor. Please try again.");
  }
};

export const fetchAasDescriptors = async (registryId, limit = 100, cursor = null) => {
  try {
    if (!registryId) {
      throw new Error("No registryId provided");
    }

    const params = {
      registryId,
      limit,
    };
    if (cursor) {
      params.cursor = cursor;
    }

    const response = await apiClient.get('/api/v1/shell-descriptors', { params });

    if (!response.data) {
      throw new Error("Unexpected empty response from server");
    }

    const { paging_metadata, result } = response.data;

    return {
      items: Array.isArray(result) ? result : [],
      nextCursor: paging_metadata?.cursor || null,
    };
  } catch (error) {
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data || error.message;
      throw new Error(`Failed to fetch AAS descriptors. HTTP ${status}: ${message}`);
    }
    throw new Error("Failed to fetch AAS descriptors. Please try again.");
  }
};
